import { FC, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  FieldsRow,
  FieldsSection,
  FormCheckbox,
  FormTextFieldWithButton,
} from '../../../../../../shared/components/form';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import { useLoaderData } from 'react-router-dom';
import { emailTabState } from '../../../states/email.state';
import { TCompanyConfigs } from '../../../../../../shared/services/companyConfig/companyConfig.service';
import TranslateIcon from '@mui/icons-material/Translate';
import { TranslationPopup } from '../../../../../../shared/components/popup/components/translation/translation.popup';
import { C_Content_Translation_Type } from '../../../../../../graphql/generatedModel';
import { ITabState } from '../../../states/tabState.model';

export const EmailTab: FC = () => {
  const loaderData = useLoaderData() as Partial<ITabState>;
  const { translationLanguages = [] } = loaderData;

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: loaderData as Partial<TCompanyConfigs> & Partial<ITabState>,
  });
  const { t } = useTranslation();

  const {
    formState: { errors, dirtyFields },
    getValues,
    setValue,
    resetField,
  } = formMethods;

  const orderTranslationPopup = useMemo(() => new TranslationPopup(), []);
  const offerTranslationPopup = useMemo(() => new TranslationPopup(), []);
  const invoiceTranslationPopup = useMemo(() => new TranslationPopup(), []);
  const debtorTranslationPopup = useMemo(() => new TranslationPopup(), []);

  useEffect(() => {
    const formStateSub = formMethods.watch((data, reason) => {
      if (reason.type === 'change' && reason.name) {
        const fieldName = reason.name as keyof TCompanyConfigs;
        const dataToSave = { [fieldName]: data[fieldName] };
        emailTabState.pub.recordData(dataToSave);
      }
    });

    const emailTabStateSub = emailTabState.sub.state().subscribe(({ dataToSave }) => {
      for (const valuesKey in dataToSave) {
        if (valuesKey && dataToSave) {
          resetField(valuesKey as keyof TCompanyConfigs, {
            defaultValue: dataToSave[valuesKey as keyof typeof dataToSave],
            keepDirty: false,
          });
        }
      }
    });

    const wireTranslationPopup = (
      popup: TranslationPopup,
      translationsField: keyof TCompanyConfigs,
    ) => {
      return popup.stream.state().subscribe((v) => {
        if (v?.action === 'reset') {
          setValue(translationsField as any, v?.values?.translations || []);
        }
        if (v?.action === 'recordData') {
          setValue(translationsField as any, v?.values?.translations || [], { shouldDirty: true });
          emailTabState.pub.recordData({ [translationsField]: v?.values?.translations || [] });
        }
        if (v?.action === 'save') {
          emailTabState.pub.saveData();
        }
      });
    };

    const orderTranslationSub = wireTranslationPopup(
      orderTranslationPopup,
      'orderEmailTextTranslations',
    );
    const offerTranslationSub = wireTranslationPopup(
      offerTranslationPopup,
      'offerEmailTextTranslations',
    );
    const invoiceTranslationSub = wireTranslationPopup(
      invoiceTranslationPopup,
      'invoiceEmailTextTranslations',
    );
    const debtorTranslationSub = wireTranslationPopup(
      debtorTranslationPopup,
      'debtReminderEmailTextTranslations',
    );

    return () => {
      formStateSub.unsubscribe();
      emailTabStateSub.unsubscribe();
      orderTranslationSub.unsubscribe();
      offerTranslationSub.unsubscribe();
      invoiceTranslationSub.unsubscribe();
      debtorTranslationSub.unsubscribe();
      orderTranslationPopup.stream.unsubscribe();
      offerTranslationPopup.stream.unsubscribe();
      invoiceTranslationPopup.stream.unsubscribe();
      debtorTranslationPopup.stream.unsubscribe();
    };
  }, []);

  const onSubmit = (e: React.FocusEvent<HTMLFormElement>) => {
    const fieldName = e.target.name as keyof TCompanyConfigs;
    if (!errors[fieldName] && !!dirtyFields[fieldName]) {
      emailTabState.pub.saveData();
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form noValidate onBlur={onSubmit}>
        <Stack
          direction='row'
          useFlexGap
          spacing={5}
          sx={{
            flexWrap: 'wrap',
          }}
        >
          <FieldsSection
            title={t('invoice.invoice')}
            sx={{ width: '45%', maxWidth: '700px' }}
            titleBoxSx={{ pt: 0 }}
            nomt
          >
            <FieldsRow>
              <FormCheckbox
                name='isDefaultEmailTextForInvoice'
                label={t('settings.use_standard_text')}
              />
            </FieldsRow>
            <FieldsRow>
              <FormTextFieldWithButton
                name='customEmailTextForInvoice'
                label={t('settings.email_text')}
                multiline
                disabled={getValues('isDefaultEmailTextForInvoice')}
                minRows={4}
                width='100%'
                buttonVisibility={
                  getValues('isDefaultEmailTextForInvoice') ? 'never' : 'hoverOrFocus'
                }
                buttonIcon={<TranslateIcon />}
                buttonTooltip={t('common.translations')}
                onButtonClick={() =>
                  invoiceTranslationPopup.stream.emit('open', {
                    values: {
                      translations: (getValues('invoiceEmailTextTranslations') as any) ?? [],
                    },
                    languages: translationLanguages,
                    contentType: C_Content_Translation_Type.CONFIGURATION_INVOICE_EMAIL_TEXT,
                    originalContent: getValues('customEmailTextForInvoice') ?? '',
                    isRichText: false,
                    minRows: 4,
                  })
                }
              />
            </FieldsRow>
          </FieldsSection>
          <FieldsSection
            title={t('debitor.debitor')}
            sx={{ width: '45%', maxWidth: '700px' }}
            titleBoxSx={{ pt: 0 }}
            nomt
          >
            <FieldsRow>
              <FormCheckbox
                name='isDefaultEmailTextForDebtor'
                label={t('settings.use_standard_text')}
              />
            </FieldsRow>
            <FieldsRow>
              <FormTextFieldWithButton
                name='customEmailTextForDebtor'
                label={t('settings.email_text')}
                multiline
                disabled={getValues('isDefaultEmailTextForDebtor')}
                minRows={4}
                width='100%'
                buttonVisibility={
                  getValues('isDefaultEmailTextForDebtor') ? 'never' : 'hoverOrFocus'
                }
                buttonIcon={<TranslateIcon />}
                buttonTooltip={t('common.translations')}
                onButtonClick={() =>
                  debtorTranslationPopup.stream.emit('open', {
                    values: {
                      translations: (getValues('debtReminderEmailTextTranslations') as any) ?? [],
                    },
                    languages: translationLanguages,
                    contentType: C_Content_Translation_Type.CONFIGURATION_DEBT_REMINDER_EMAIL_TEXT,
                    originalContent: getValues('customEmailTextForDebtor') ?? '',
                    isRichText: false,
                    minRows: 4,
                  })
                }
              />
            </FieldsRow>
          </FieldsSection>
          <FieldsSection
            title={t('order.order')}
            sx={{ width: '45%', maxWidth: '700px' }}
            titleBoxSx={{ pt: 0 }}
            nomt
          >
            <FieldsRow>
              <FormCheckbox
                name='isDefaultEmailTextForOrder'
                label={t('settings.use_standard_text')}
              />
            </FieldsRow>
            <FieldsRow>
              <FormTextFieldWithButton
                name='customEmailTextForOrder'
                label={t('settings.email_text')}
                multiline
                disabled={getValues('isDefaultEmailTextForOrder')}
                minRows={4}
                width='100%'
                buttonVisibility={
                  getValues('isDefaultEmailTextForOrder') ? 'never' : 'hoverOrFocus'
                }
                buttonIcon={<TranslateIcon />}
                buttonTooltip={t('common.translations')}
                onButtonClick={() =>
                  orderTranslationPopup.stream.emit('open', {
                    values: {
                      translations: (getValues('orderEmailTextTranslations') as any) ?? [],
                    },
                    languages: translationLanguages,
                    contentType: C_Content_Translation_Type.CONFIGURATION_ORDER_EMAIL_TEXT,
                    originalContent: getValues('customEmailTextForOrder') ?? '',
                    isRichText: false,
                    minRows: 4,
                  })
                }
              />
            </FieldsRow>
          </FieldsSection>
          <FieldsSection
            title={t('offer.offer')}
            sx={{ width: '45%', maxWidth: '700px' }}
            titleBoxSx={{ pt: 0 }}
            nomt
          >
            <FieldsRow>
              <FormCheckbox
                name='isDefaultEmailTextForOffer'
                label={t('settings.use_standard_text')}
              />
            </FieldsRow>
            <FieldsRow>
              <FormTextFieldWithButton
                name='customEmailTextForOffer'
                label={t('settings.email_text')}
                multiline
                disabled={getValues('isDefaultEmailTextForOffer')}
                minRows={4}
                width='100%'
                buttonVisibility={
                  getValues('isDefaultEmailTextForOffer') ? 'never' : 'hoverOrFocus'
                }
                buttonIcon={<TranslateIcon />}
                buttonTooltip={t('common.translations')}
                onButtonClick={() =>
                  offerTranslationPopup.stream.emit('open', {
                    values: {
                      translations: (getValues('offerEmailTextTranslations') as any) ?? [],
                    },
                    languages: translationLanguages,
                    contentType: C_Content_Translation_Type.CONFIGURATION_OFFER_EMAIL_TEXT,
                    originalContent: getValues('customEmailTextForOffer') ?? '',
                    isRichText: false,
                    minRows: 4,
                  })
                }
              />
            </FieldsRow>
          </FieldsSection>
        </Stack>
      </form>
      <orderTranslationPopup.Component />
      <offerTranslationPopup.Component />
      <invoiceTranslationPopup.Component />
      <debtorTranslationPopup.Component />
    </FormProvider>
  );
};
